import type {
  CompositionV2Entity,
  CompositionV3Ent,
  PayV3Req,
} from '@revelio/data-access';

import {
  useFilterContext,
  useIsTaxonomyResolved,
} from '../../../filters/contexts/FilterProvider';
import { useEffectiveFilters } from '../../../filters/hooks/useEffectiveFilters';
import type {
  PrimaryFilterId,
  TreeSelection,
} from '../../../filters/types/filters';
import { resolveV3EntForSelection } from '../../../filters/utils/get-v3-entities';
import { getPayV3Variables } from '../../../filters/utils/pay-v3/get-pay-v3-variables';
import { mapPositionsEntitiesToCompositionV3 } from '../../../filters/utils/role-skill-ent-kind';
import { useChart } from '../../Chart/ChartProvider';
import { useTabContentConfig } from '../../PageConfigProvider/PageConfigProvider';
import type { CompensationChartType } from '../../PageConfigProvider/types/chart';
import type {
  CompensationSecondaryDimension,
  SecondaryDimensionValues,
} from '../../PageConfigProvider/types/dimension';
import type { PayMetricValues } from '../../PageConfigProvider/types/metric';
import { isOverviewPage } from '../../PageConfigProvider/utils/isOverviewPage';
import { usePageState } from '../../PageState/PageStateProvider';
import {
  type BoxSeriesDataSet,
  type BreakdownBoxDataSet,
  type BreakdownSingleDataSet,
  type DataProviderChildren,
  DataProviderQueryStatus,
  type DataProviderSet,
  type DistributionDataSet,
  type SingleDataSet,
  type TimeseriesDataSet,
} from '../types';
import {
  convertPayData,
  type PayResponse,
} from '../utils/convert-compensation-data';
import { CompensationTopCompetitorsDataProvider } from './CompensationTopCompetitorsDataProvider';
import { CompensationTopNDataProvider } from './CompensationTopNDataProvider';
import { usePayData } from './use-pay-data';

export type CompensationData =
  | SingleDataSet
  | TimeseriesDataSet
  | BoxSeriesDataSet
  | BreakdownBoxDataSet
  | BreakdownSingleDataSet
  | DistributionDataSet;

const UNADJUSTED_ONLY_DISTRIBUTION_METRICS: readonly PayMetricValues[] = [
  'total_compensation',
  'contractor_pay_rate',
  'base_salary',
];

const isUnadjustedOnlyDistribution = ({
  chartType,
  metric,
}: {
  chartType: CompensationChartType;
  metric: PayMetricValues;
}) =>
  chartType === 'distribution' &&
  UNADJUSTED_ONLY_DISTRIBUTION_METRICS.includes(metric);

/** Raw (unadjusted) data drops the weighting. */
const withRawOverride = (variables: PayV3Req): PayV3Req => ({
  ...variables,
  corrections: { ...variables.corrections, with_weight: false },
});

type CompensationDataProviderProps = {
  metric: PayMetricValues;
  primaryDimension: PrimaryFilterId;
  topNDimension?: SecondaryDimensionValues;
  secondaryDimension?: CompensationSecondaryDimension;
  chartType: CompensationChartType;
  exchangeRate?: number;
  entitiesOverride?: CompositionV2Entity[];
  children: DataProviderChildren<DataProviderSet | undefined>;
};

export const CompensationDataProvider = ({
  metric,
  primaryDimension,
  topNDimension,
  secondaryDimension,
  chartType,
  exchangeRate,
  entitiesOverride,
  children,
}: CompensationDataProviderProps) => {
  // Pinned to 'month' like every other pay provider: `pay_v3` is a monthly
  // series and the encoder formats dates as `yyyy-MM`. Pages mixing pay with
  // postings derive a page-wide 'week' granularity for their postings charts,
  // and those week bounds truncate into months the monthly data does not
  // cover — postings run fresher than the monthly aggregates.
  const effectiveFilters = useEffectiveFilters({ viewGranularity: 'month' });
  const { item } = useChart();
  const { pageState } = usePageState();
  const { revelioPageId } = useTabContentConfig();
  const { usesStateBreakdownOverMsa } = useFilterContext();

  const useTopGeosOnly =
    isOverviewPage(revelioPageId) &&
    chartType === 'box' &&
    secondaryDimension === 'geography';

  if (topNDimension) {
    if (topNDimension === 'competitors') {
      return (
        <CompensationTopCompetitorsDataProvider
          primaryDimension={primaryDimension}
          metric={metric}
          chartType={chartType}
          exchangeRate={exchangeRate}
        >
          {children}
        </CompensationTopCompetitorsDataProvider>
      );
    }
  }

  if (
    (chartType === 'grouped-bar' || chartType === 'stacked-bar') &&
    (secondaryDimension === 'geography' || secondaryDimension === 'company')
  ) {
    return (
      <CompensationTopNDataProvider
        primaryDimension={primaryDimension}
        secondaryDimension={secondaryDimension}
        metric={metric}
        exchangeRate={exchangeRate}
      >
        {children}
      </CompensationTopNDataProvider>
    );
  }

  const selectedMultiEntities = pageState.multiPrimaryEntities?.[item?.id];

  const mappedEntitiesOverride = entitiesOverride
    ? (mapPositionsEntitiesToCompositionV3(entitiesOverride) ?? [])
    : undefined;
  const multiPrimaryEntities = getMultiPrimaryEntities({
    chartType,
    selectedEntities: selectedMultiEntities,
  });
  const baseVariables = getPayV3Variables({
    primaryDimension,
    effectiveFilters,
    entitiesOverride:
      mappedEntitiesOverride ?? multiPrimaryEntities ?? undefined,
  });
  const variablesWithGeoOption: PayV3Req = {
    ...baseVariables,
    ...(usesStateBreakdownOverMsa && {
      options: { geo_breakdown_state_over_msa: true },
    }),
  };
  const finalVariables = isUnadjustedOnlyDistribution({ chartType, metric })
    ? withRawOverride(variablesWithGeoOption)
    : variablesWithGeoOption;

  if (!finalVariables.filters.start_date || !finalVariables.filters.end_date) {
    return children({ data: undefined, status: DataProviderQueryStatus.Idle });
  }

  return (
    <CompensationQuery
      variant={useTopGeosOnly ? 'topGeos' : 'full'}
      req={finalVariables}
    >
      {({ data, status }) =>
        children({
          data: data?.pay_v3
            ? convertPayData({
                data,
                chartType,
                metric,
                secondaryDimension,
                exchangeRate,
              })
            : undefined,
          status,
        })
      }
    </CompensationQuery>
  );
};

const CompensationQuery = ({
  variant,
  req,
  children,
}: {
  variant: 'full' | 'topGeos';
  req: PayV3Req;
  children: DataProviderChildren<PayResponse | undefined>;
}) => {
  const isTaxonomyResolved = useIsTaxonomyResolved();
  const { data, status } = usePayData({
    variant,
    req,
    pause: !isTaxonomyResolved,
  });

  return children({ data, status });
};

/**
 * Narrows the pay entities to the Multi Primary Entity switcher's selections,
 * resolving the `{ id, levelId }` selections straight to `CompositionV3Ent`.
 * Returns null when the narrowing doesn't apply (non-box chart, no selection,
 * nothing resolvable) so the caller falls back to the primary-filter entities.
 */
const getMultiPrimaryEntities = ({
  chartType,
  selectedEntities,
}: {
  chartType: CompensationChartType;
  selectedEntities?: Array<{ id: string; levelId: string; name: string }>;
}): CompositionV3Ent[] | null => {
  if (
    chartType !== 'box' ||
    !selectedEntities ||
    selectedEntities.length === 0
  ) {
    return null;
  }
  const entities = selectedEntities
    .map((selection) =>
      resolveV3EntForSelection({
        id: selection.id,
        levelId: selection.levelId as TreeSelection['levelId'],
      })
    )
    .filter((ent): ent is CompositionV3Ent => ent !== null);
  return entities.length > 0 ? entities : null;
};
